
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/supabase.js';

class SupabaseClient {
  constructor(url, key) {
    this.url = url;
    this.key = key;
    this.session = null;
  }

  async signUp(email, password) {
    const response = await fetch(`${this.url}/auth/v1/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': this.key
      },
      body: JSON.stringify({ email, password })
    });
    const data = await response.json();
    if (!response.ok) {
      // normalize error shape
      return { error: { message: data.error_description || data.error || data.message || 'Sign up failed' } };
    }
    return data;
  }

  async signIn(email, password) {
    const response = await fetch(`${this.url}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': this.key
      },
      body: JSON.stringify({ email, password })
    });
    const data = await response.json();
    if (!response.ok) {
      return { error: { message: data.error_description || data.error || data.message || 'Authentication failed' } };
    }
    if (data.access_token) {
      this.session = data;
      localStorage.setItem('supabase_session', JSON.stringify(data));
    }
    return data;
  }

  async signInWithOAuth(provider) {
    const redirectTo = `${window.location.origin}`;
    const authUrl = `${this.url}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(redirectTo)}`;
    window.location.href = authUrl;
  }

  async signOut() {
    this.session = null;
    localStorage.removeItem('supabase_session');
  }

  async deleteAccount() {
    const session = this.getSession();
    const userId = session?.user?.id;
    
    if (!userId) {
      throw new Error('No authenticated user to delete');
    }

    const bearer = session?.access_token || null;
    if (!bearer) {
      throw new Error('No access token available');
    }

    // Delete user account using Supabase Admin API
    // Note: This requires the service_role key for admin operations
    // Since we can't use service_role in frontend, we'll use the auth API to delete the user
    const response = await fetch(`${this.url}/auth/v1/user`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${bearer}`,
        'apikey': this.key
      }
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete account: ${error}`);
    }

    // Clear session after deletion
    this.session = null;
    localStorage.removeItem('supabase_session');
    localStorage.removeItem('facebook_token');
    localStorage.removeItem('youtube_token');
    localStorage.removeItem('tiktok_auth');

    return true;
  }

  getSession() {
    if (this.session) return this.session;
    const stored = localStorage.getItem('supabase_session');
    if (stored) {
      this.session = JSON.parse(stored);
      return this.session;
    }
    return null;
  }

  getUser() {
    const session = this.getSession();
    return session?.user || null;
  }

  async handleOAuthCallback() {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    
    if (accessToken) {
      const session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        user: { email: 'oauth-user' }
      };
      
      this.session = session;
      localStorage.setItem('supabase_session', JSON.stringify(session));
      window.history.replaceState({}, document.title, window.location.pathname);
      return session;
    }
    return null;
  }

  // Upsert a platform token into Supabase via REST. Requires a `platform_tokens` table.
  // Expected table columns: id (uuid), user_id (text), platform (text), access_token (text), refresh_token (text), expires_at (timestamp), meta (jsonb), created_at, updated_at
  // Make sure to add RLS policies so users can only insert/select their own rows.
  async upsertPlatformToken({ user_id, platform, access_token, refresh_token = null, expires_at = null, meta = null }) {
    if (!user_id || !platform || !access_token) {
      throw new Error('user_id, platform and access_token are required for upsertPlatformToken');
    }

    const session = this.getSession();
    const bearer = session?.access_token || null;

    const url = `${this.url}/rest/v1/platform_tokens?on_conflict=platform,user_id`;
    const body = [{ user_id, platform, access_token, refresh_token, expires_at, meta }];

    const headers = {
      'Content-Type': 'application/json',
      apikey: this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`Failed to upsert platform token: ${resp.status} ${txt}`);
    }

    // Supabase may return 201 with empty body or 204 No Content
    const text = await resp.text();
    const data = text ? JSON.parse(text) : { success: true };
    return data;
  }

  // Fetch platform tokens for a user
  async fetchPlatformTokens(user_id) {
    if (!user_id) return [];
    const session = this.getSession();
    const bearer = session?.access_token || null;
    const url = `${this.url}/rest/v1/platform_tokens?user_id=eq.${encodeURIComponent(user_id)}`;
    const headers = {
      apikey: this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, { headers });
    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`Failed to fetch platform tokens: ${resp.status} ${txt}`);
    }
    const data = await resp.json();
    return data;
  }

  // Delete a platform token for a user
  async deletePlatformToken(user_id, platform) {
    if (!user_id || !platform) {
      throw new Error('user_id and platform are required for deletePlatformToken');
    }

    const session = this.getSession();
    const bearer = session?.access_token || null;
    const url = `${this.url}/rest/v1/platform_tokens?user_id=eq.${encodeURIComponent(user_id)}&platform=eq.${encodeURIComponent(platform)}`;
    const headers = {
      apikey: this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, {
      method: 'DELETE',
      headers
    });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`Failed to delete platform token: ${resp.status} ${txt}`);
    }

    return true;
  }

  // =====================================================
  // PASSWORD MANAGEMENT
  // =====================================================

  /**
   * Update password for the current authenticated user
   * @param {string} newPassword - The new password (min 6 chars)
   */
  async updatePassword(newPassword) {
    const session = this.getSession();
    const bearer = session?.access_token;

    if (!bearer) {
      throw new Error('No authenticated user');
    }

    if (!newPassword || newPassword.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }

    const response = await fetch(`${this.url}/auth/v1/user`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearer}`,
        'apikey': this.key
      },
      body: JSON.stringify({ password: newPassword })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || error.message || 'Failed to update password');
    }

    return { success: true };
  }

  /**
   * Send a password reset email to the specified email address
   * @param {string} email - The email address to send the reset link to
   */
  async sendPasswordResetEmail(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    const redirectTo = `${window.location.origin}/login`;

    const response = await fetch(`${this.url}/auth/v1/recover`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': this.key
      },
      body: JSON.stringify({ email, redirect_to: redirectTo })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || error.message || 'Failed to send reset email');
    }

    return { success: true };
  }

  // =====================================================
  // SUBSCRIPTION MANAGEMENT
  // =====================================================

  /**
   * Get the current user's subscription
   */
  async getSubscription() {
    const session = this.getSession();
    const userId = session?.user?.id;
    
    if (!userId) {
      return null;
    }

    const bearer = session?.access_token;
    const url = `${this.url}/rest/v1/subscriptions?user_id=eq.${encodeURIComponent(userId)}&select=*`;
    
    const headers = {
      'apikey': this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, { headers });
    
    if (!resp.ok) {
      console.error('Failed to fetch subscription:', await resp.text());
      return null;
    }

    const data = await resp.json();
    return data[0] || null;
  }

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans() {
    const session = this.getSession();
    const bearer = session?.access_token;
    
    const url = `${this.url}/rest/v1/subscription_plans?is_active=eq.true&order=sort_order.asc&select=*`;
    
    const headers = {
      'apikey': this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, { headers });
    
    if (!resp.ok) {
      console.error('Failed to fetch plans:', await resp.text());
      return [];
    }

    return await resp.json();
  }

  /**
   * Get the current user's invoices
   */
  async getInvoices() {
    const session = this.getSession();
    const userId = session?.user?.id;
    
    if (!userId) {
      return [];
    }

    const bearer = session?.access_token;
    const url = `${this.url}/rest/v1/invoices?user_id=eq.${encodeURIComponent(userId)}&order=created_at.desc&select=*`;
    
    const headers = {
      'apikey': this.key
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, { headers });
    
    if (!resp.ok) {
      console.error('Failed to fetch invoices:', await resp.text());
      return [];
    }

    return await resp.json();
  }

  /**
   * Create initial free subscription for user (called after signup if trigger didn't fire)
   */
  async ensureFreeSubscription() {
    const session = this.getSession();
    const userId = session?.user?.id;
    
    if (!userId) {
      return null;
    }

    // Check if subscription already exists
    const existing = await this.getSubscription();
    if (existing) {
      return existing;
    }

    // Create free subscription
    const bearer = session?.access_token;
    const url = `${this.url}/rest/v1/subscriptions`;
    
    const headers = {
      'Content-Type': 'application/json',
      'apikey': this.key,
      'Prefer': 'return=representation'
    };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user_id: userId,
        plan: 'free',
        status: 'active'
      })
    });

    if (!resp.ok) {
      console.error('Failed to create subscription:', await resp.text());
      return null;
    }

    const data = await resp.json();
    return data[0] || null;
  }

  // Generic query builder for REST API
  from(table) {
    const session = this.getSession();
    const bearer = session?.access_token || null;
    const baseUrl = `${this.url}/rest/v1/${table}`;
    
    return {
      insert: (data) => {
        const headers = {
          'Content-Type': 'application/json',
          'apikey': this.key,
          'Prefer': 'return=representation'
        };
        if (bearer) headers.Authorization = `Bearer ${bearer}`;

        const body = Array.isArray(data) ? data : [data];
        
        return {
          select: () => ({
            single: async () => {
              const resp = await fetch(baseUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(body)
              });

              const responseData = await resp.json();
              
              if (!resp.ok) {
                return { data: null, error: responseData };
              }

              return { data: responseData[0] || null, error: null };
            }
          }),
          // Direct execution without select
          then: async (resolve) => {
            const resp = await fetch(baseUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify(body)
            });

            const responseData = await resp.json();
            
            if (!resp.ok) {
              resolve({ data: null, error: responseData });
            } else {
              resolve({ data: responseData, error: null });
            }
          }
        };
      },

      select: (columns = '*') => {
        let query = `${baseUrl}?select=${columns}`;
        let orderBy = null;
        let filters = [];

        const queryObject = {
          eq: (column, value) => {
            filters.push(`${column}=eq.${encodeURIComponent(value)}`);
            return queryObject;
          },
          order: (column, options = {}) => {
            orderBy = `${column}.${options.ascending ? 'asc' : 'desc'}`;
            return queryObject;
          },
          then: async (resolve) => {
            const headers = {
              'apikey': this.key
            };
            if (bearer) headers.Authorization = `Bearer ${bearer}`;
            if (filters.length > 0) query += `&${filters.join('&')}`;
            if (orderBy) query += `&order=${orderBy}`;

            const resp = await fetch(query, { headers });
            const responseData = await resp.json();

            if (!resp.ok) {
              resolve({ data: null, error: responseData });
            } else {
              resolve({ data: responseData, error: null });
            }
          }
        };

        return queryObject;
      },

      delete: () => {
        let query = baseUrl;
        
        return {
          eq: (column, value) => {
            query += `?${column}=eq.${encodeURIComponent(value)}`;
            return {
              then: async (resolve) => {
                const headers = {
                  'apikey': this.key
                };
                if (bearer) headers.Authorization = `Bearer ${bearer}`;

                const resp = await fetch(query, {
                  method: 'DELETE',
                  headers
                });

                if (!resp.ok) {
                  const error = await resp.json();
                  resolve({ data: null, error });
                } else {
                  resolve({ data: null, error: null });
                }
              }
            };
          }
        };
      }
    };
  }
}

const supabase = new SupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export { supabase };